import { useEffect, useState, type FormEvent } from 'react';
import Modal, { inputCls, labelCls } from '@/components/base/Modal';
import {
  fetchSupplierPayments,
  fetchSupplierBalance,
  addSupplierPayment,
  deleteSupplierPayment,
  type SupplierPayment,
  type SupplierBalance,
} from '@/services/suppliers';

interface SupplierDetailModalProps {
  supplierId: string;
  supplierName: string;
  onClose: () => void;
  onSaved: () => void;
}

const STATUS_META: Record<string, { label: string; color: string; bg: string }> = {
  'Al día': { label: 'Al día', color: 'text-emerald-700', bg: 'bg-emerald-50' },
  'Pendiente de pago': { label: 'Pendiente de pago', color: 'text-amber-700', bg: 'bg-amber-50' },
  'Pago parcial': { label: 'Pago parcial', color: 'text-orange-700', bg: 'bg-orange-50' },
};

function statusMeta(status: string) {
  return STATUS_META[status] || { label: status || 'Sin estado', color: 'text-foreground-600', bg: 'bg-background-100' };
}

export default function SupplierDetailModal({
  supplierId,
  supplierName,
  onClose,
  onSaved,
}: SupplierDetailModalProps) {
  const [balance, setBalance] = useState<SupplierBalance | null>(null);
  const [payments, setPayments] = useState<SupplierPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState('');
  const [paidAt, setPaidAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function load() {
    setLoading(true);
    const [b, p] = await Promise.all([
      fetchSupplierBalance(supplierId),
      fetchSupplierPayments(supplierId),
    ]);
    setBalance(b);
    setPayments(p);
    setLoading(false);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierId]);

  const meta = statusMeta(balance?.status ?? '');

  async function handleAddPayment(e: FormEvent) {
    e.preventDefault();
    setError('');
    const amt = Number(amount) || 0;
    if (amt <= 0) {
      setError('El monto debe ser mayor a cero.');
      return;
    }
    setSaving(true);
    const { error: addError } = await addSupplierPayment({
      supplierId,
      amount: amt,
      paidAt: paidAt || '',
      notes,
    });
    setSaving(false);
    if (addError) {
      setError(addError);
      return;
    }
    setAmount('');
    setNotes('');
    await load();
    onSaved();
  }

  async function handleDeletePayment(id: string) {
    const { error: delError } = await deleteSupplierPayment(id, supplierId);
    if (!delError) {
      await load();
      onSaved();
    }
  }

  function formatDate(value: string) {
    if (!value) return '—';
    return new Date(value).toLocaleDateString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }

  return (
    <Modal
      title="Estado y abonos del proveedor"
      subtitle={supplierName}
      onClose={onClose}
      maxWidth="max-w-2xl"
    >
      {loading ? (
        <div className="text-center py-12 text-foreground-400">
          <i className="ri-loader-4-line animate-spin text-2xl mr-2"></i>Cargando...
        </div>
      ) : (
        <div className="space-y-5">
          {/* Resumen de saldo */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-background-50 rounded-lg p-3 text-center">
              <p className="text-xs text-foreground-500">Compras (deuda)</p>
              <p className="text-lg font-bold text-foreground-950 mt-0.5">
                ${(balance?.totalPurchases ?? 0).toLocaleString()}
              </p>
            </div>
            <div className="bg-background-50 rounded-lg p-3 text-center">
              <p className="text-xs text-foreground-500">Abonado</p>
              <p className="text-lg font-bold text-emerald-600 mt-0.5">
                ${(balance?.totalPaid ?? 0).toLocaleString()}
              </p>
            </div>
            <div className="bg-background-50 rounded-lg p-3 text-center">
              <p className="text-xs text-foreground-500">Saldo</p>
              <p className="text-lg font-bold text-amber-600 mt-0.5">
                ${(balance?.remaining ?? 0).toLocaleString()}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium whitespace-nowrap ${meta.color} ${meta.bg}`}>
              {meta.label}
            </span>
          </div>

          {/* Abonos */}
          <div>
            <p className="text-xs font-semibold text-foreground-700 mb-2">Abonos registrados</p>
            {payments.length === 0 ? (
              <p className="text-sm text-foreground-400">Sin abonos registrados.</p>
            ) : (
              <div className="space-y-2 max-h-[180px] overflow-y-auto">
                {payments.map((p) => (
                  <div
                    key={p.id}
                    className="flex items-center gap-3 bg-white border border-background-200 rounded-lg px-3 py-2"
                  >
                    <div className="w-8 h-8 rounded-lg bg-accent-50 flex items-center justify-center flex-shrink-0">
                      <i className="ri-bank-card-line text-accent-600"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground-900">${p.amount.toLocaleString()}</p>
                      <p className="text-xs text-foreground-500 truncate">
                        {formatDate(p.paidAt)}
                        {p.notes ? ` · ${p.notes}` : ''}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDeletePayment(p.id)}
                      className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:bg-red-50 hover:text-red-600 transition-colors cursor-pointer"
                      aria-label="Eliminar abono"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Form de abono */}
          <form
            onSubmit={handleAddPayment}
            className="space-y-3 bg-background-50 border border-background-200 rounded-lg p-4"
          >
            <p className="text-xs font-semibold text-foreground-700">Registrar abono</p>
            {error && (
              <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
                <i className="ri-error-warning-line"></i>
                <span>{error}</span>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Monto ($) *</label>
                <input
                  type="number"
                  min="0"
                  className={inputCls}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>
              <div>
                <label className={labelCls}>Fecha</label>
                <input
                  type="date"
                  className={inputCls}
                  value={paidAt}
                  onChange={(e) => setPaidAt(e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className={labelCls}>Nota</label>
              <input
                className={inputCls}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Abono parcial, transferencia..."
                maxLength={200}
              />
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className="text-sm px-4 py-2 bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {saving ? 'Guardando...' : 'Agregar abono'}
              </button>
            </div>
          </form>
        </div>
      )}
    </Modal>
  );
}